import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shell } from './components/Cinema';
import { Play, Pause } from 'lucide-react';

const SITE_URL = 'https://mgn1point6.com';

// Song data with lyrics
const SONGS_DATA = [
  {
    id: 1,
    title: 'Do Not Sing That Song',
    chapter: 'The Invitation · Chapter 1',
    subtitle: 'The Lullaby She Was Told Not To Sing',
    audioUrl: 'https://customer-assets-jai6qajn.emergentagent.net/job_author-hub-59/artifacts/t4m3nei5_song%201.wav',
    beatSheet: [
      'Battersea, 1892, 11:47 PM. Rain. Coal. Mud.',
      'Power station half-built, skeletal, watching the attic.',
      'A choir of children hums off by seven cents flat.',
      'Mary Darling, fourteen, mends match-factory uniforms.',
      'Two brothers asleep. She sings them down.',
      'Not a nursery rhyme — a Welsh cradle of vowels: A-E-I-O-U-Y.',
      'Candle flame ripples. Sand on the sill draws a hexagon.',
      'Mrs. Darling slams the window.',
      'House rule: we do not sing that song in this house.',
      '1871: twelve workhouse children vanished at choir practice.',
      'Official ledger: cholera.',
      'Mother to mother: they followed a boy who never aged.',
      'Into abandoned pleasure gardens in the swamp.',
      'Mudlarks called it Neverland.',
      "Mrs. Darling's brother was one of the twelve.",
      'She still sets a plate.',
      'Mary is logical. She does not believe.',
    ],
    sections: [
      {
        type: 'intro',
        title: 'Intro · Spoken over rain and a detuned hum',
        lines: [
          'Battersea. Eleven forty-seven.',
          'Rain writing the same sentence on the brick.',
          'Coal in the throat. Mud at the teeth.',
          'A power station half-born, iron ribs, no breath.',
          'Count it in: A… E… I…',
          "Don't finish it.",
        ],
      },
      {
        type: 'verse',
        title: 'Verse · Mary in the attic',
        lines: [
          'Fourteen, needle-finger, match-girl hems,',
          "Stitching other children's work into her brothers' beds.",
          'Clerk\'s daughter, attic lean, candle on a plate,',
          'Two small chests rising, London holding its breath late.',
          "She don't know a spell from a scale from a hymn,",
          'She only know the old mouth her mother put in.',
          'Welsh and quiet. Vowels only. No consonant knife.',
          'A-E-I-O-U-Y — the long way home through a short life.',
        ],
      },
      {
        type: 'chorus',
        title: 'Chorus / Hook · The house rule',
        lines: [
          'We do not sing that song in this house.',
          'Twelve empty chairs and a plate for a mouth.',
          'Official cholera. Unofficial truth.',
          'We do not sing that song in this house.',
        ],
      },
      {
        type: 'verse',
        title: 'Verse · The flame answers',
        lines: [
          'Flame lean left when the Y come round,',
          'Sand on the sill get up off the ground.',
          "Hexagon. Clean. Like a lock learning its key,",
          "Geometry in grit where a child's breath used to be.",
          'Mary watch it happen, logic still in the chair,',
          "Wind don't draw six sides in the London air.",
          "She keep singing anyway — that's how you put boys down,",
          "That's how you keep the dark from climbing the town.",
        ],
      },
      {
        type: 'rap',
        title: 'Long Rap · The storyline — chronological, educational',
        lines: [
          "Year of our Lord eighteen ninety-two, last hour of the clock,",
          "World B, particle London, soot on the frock.",
          "Battersea Power Station ain't powered yet — it's a ribcage in rain,",
          "Camera in the mud with the uncounted names.",
          "Offscreen: a children's choir, seven cents flat of the key,",
          "That's not out of tune — that's the old industry.",
          "Cut attic. Mary Darling. Fourteen. Hands that know thread.",
          "Match-factory uniforms, other kids' bread.",
          "Brothers under one blanket 'cause heat is a wage,",
          "She give 'em the cradle her mother taught, pre-printed page.",
          "Not Twinkle. Not Jesus. Not God save the Queen.",
          "Six vowels in a line like a machine no one's seen.",
          "A — amen waiting. E — we end. I — I remember.",
          "O — we are one. U — you return. Y — I yield, I surrender.",
          "She sing it forward like a mother, not a door,",
          "And the candle still ripple like it heard this before.",
          "Sand stand up. Hexagon. Perfect. No child drew that.",
          "That's when the latch slam — mother's hand on the glass.",
          "We do not sing that song in this house. That's the law.",
          "Not because it's ugly. Because it was the last thing they saw.",
          "Eighteen seventy-one. Battersea workhouse. Choir.",
          "Twelve go in with their mouths open. Twelve don't retire.",
          "Paper say cholera. Paper always say cholera.",
          "Mother to mother say: a boy who don't get older.",
          "They followed him into gardens the Crown stopped mapping,",
          "Pleasure-grounds in the swamp where the fair went rotting.",
          "Mudlarks had a name for it. Neverland. Not magic. Mud.",
          "A place police don't count because counting costs blood.",
          "And Mrs. Darling's brother was number in that twelve.",
          "She still set his plate like grief was something you could shelve.",
          "Mary hear all that. Mary is logical. Mary don't bite.",
          "Dead uncles and swamp fairs don't hexagon a night.",
          "So she close her mouth. Rule accepted. Candle still wrong.",
          "That's the problem, that's the lid, that's the song under the song.",
        ],
      },
      {
        type: 'interlude',
        title: 'Interlude · Plate / choir / rain',
        lines: [
          'A plate. A name she never met.',
          "A choir that don't come home yet.",
          'Rain on the power-station bone.',
          'Hum. Flat. Seven cents. Alone.',
        ],
      },
      {
        type: 'verse',
        title: 'Verse · She does not believe',
        lines: [
          'Mary tell herself: flame is a draft, sand is a trick,',
          'Grief make a house superstitious and thick.',
          'Mama lost a brother so mama lost the key,',
          'Lock the vowels in the wall, call it family.',
          "But logic don't explain a hexagon clean,",
          "Logic don't explain why the hum sit in between.",
          'She swallow the rest of the cradle, leave Y in her teeth,',
          'Two brothers still sleeping underneath.',
        ],
      },
      {
        type: 'bridge',
        title: 'Bridge · Tragedy of the rule',
        lines: [
          'You can slam a window on a weather.',
          'You cannot slam a window on a letter.',
          'You can call a vanishing a fever.',
          'You cannot call a plate a never.',
          'The house made a law out of a wound.',
          'The wound learned to listen for a tune.',
        ],
      },
      {
        type: 'chorus',
        title: 'Chorus / Hook · Same law, other mouth — juxtaposed',
        lines: [
          'We do not sing that song in this house.',
          'But the sand on the sill still spells it out.',
          "Candle don't lie when the vowels come through.",
          'We do not sing — so the deadhouse do.',
          '',
          'We do not sing that song in this house.',
          'Twelve empty chairs and a plate for a mouth.',
          'If the rule was enough, the flame would sit still.',
          "The window is shut. The candle is not. That's the bill.",
          '',
          'The window is shut. The candle is not.',
        ],
      },
    ],
  },
  {
    id: 2,
    title: 'Window, Not a Door',
    chapter: 'The Invitation · Chapter 1',
    subtitle: 'The Bowl Speaks',
    audioUrl: 'https://customer-assets-jai6qajn.emergentagent.net/job_author-hub-59/artifacts/v6xuzmxb_2.wav',
    beatSheet: [
      'The cracked vanity is not a mirror.',
      'It is a brass cymatic bowl filled with sand.',
      'Vibrating.',
      "A boy's face in it. Beautiful. Twelve. Eternal.",
      'He smiles: This is a window, not a door.',
    ],
    sections: [
      {
        type: 'verse',
        title: 'Verse · The bowl reveals',
        lines: [
          'Mirror crack but the glass still hold,',
          'Behind it: brass, sand, stories untold.',
          "Boy's face forming in the vibration,",
          'Twelve years old for every generation.',
        ],
      },
    ],
  },
  {
    id: 3,
    title: 'You Heard the Song',
    chapter: 'The Invitation · Chapter 2',
    subtitle: 'The Boy Who Wouldn\'t Grow',
    audioUrl: 'https://customer-assets-jai6qajn.emergentagent.net/job_author-hub-59/artifacts/a8xa8ij7_3.wav',
    beatSheet: [
      "You heard the Song, didn't you?",
      'The real one. The one they told you not to sing.',
      'He sings A-E-I-O-U-Y backward in major, old flat tuning.',
      'The boys levitate. Cymatic lift.',
      'Bodies less particle, more wave.',
      'Singing forward does nothing.',
      'Singing backward opens a portal.',
    ],
    sections: [
      {
        type: 'verse',
        title: 'Verse · Pan at the window',
        lines: [
          "You heard the Song, didn't you?",
          'The real one they hid from view.',
          'Backward in major, seven cents flat,',
          "That's the key, that's the map, that's the doormat.",
        ],
      },
    ],
  },
  {
    id: 4,
    title: 'Fourteen and Fifteen',
    chapter: 'The Invitation · Chapter 2',
    subtitle: 'The Ledger Updates',
    audioUrl: 'https://customer-assets-jai6qajn.emergentagent.net/job_author-hub-59/artifacts/7aw9af96_4.wav',
    beatSheet: [
      'The bowl slams shut.',
      'In the workhouse ledger, two new boys appear.',
      'Numbers 14 and 15.',
      'Breath pulled as blue vapor toward the wheel.',
    ],
    sections: [
      {
        type: 'verse',
        title: 'Verse · The ledger',
        lines: [
          'Bowl slam shut, the crossing complete,',
          'Two new numbers on the workhouse sheet.',
          'Fourteen, fifteen, breath turned blue,',
          "Vapor feeding what the wheel spins through.",
        ],
      },
    ],
  },
  {
    id: 5,
    title: 'Unmapped',
    chapter: 'The Invitation · Chapter 3',
    subtitle: 'Neverland Is Real',
    audioUrl: 'https://customer-assets-jai6qajn.emergentagent.net/job_author-hub-59/artifacts/1kywo28m_5.wav',
    beatSheet: [
      'This place is not imaginary.',
      "It is the real Neverland from 1870s maps.",
      "Unmapped swamp where the 1862 World's Fair failed.",
      'The Crown stopped surveying because the mud took it.',
      'To police, it did not exist.',
      'To lost children, paradise.',
    ],
    sections: [
      {
        type: 'verse',
        title: 'Verse · The real Neverland',
        lines: [
          "Thirty acres the Crown couldn't count,",
          "World's Fair bones in the swamp amount.",
          "Police don't map what police don't own,",
          "Lost children found a place called home.",
        ],
      },
    ],
  },
  {
    id: 6,
    title: 'Thirteen Then Flat',
    chapter: 'The Ritual',
    subtitle: 'The Riddle',
    audioUrl: null,
    beatSheet: [
      'Thirteen original tones, not twelve.',
      'The church kept twelve to make the choir sound perfect.',
      'But perfect is a trap. Twelve spins forever.',
      'Thirteen resolves.',
      'Stop wheel + sing forwards = riddle solved.',
    ],
    sections: [
      {
        type: 'verse',
        title: 'Verse · The thirteenth tone',
        lines: [
          'Twelve tones spin the wheel forever,',
          'Thirteen breaks the endless tether.',
          'Church hid the last to keep control,',
          'The silence after is what makes you whole.',
        ],
      },
    ],
  },
  {
    id: 7,
    title: 'Bio · Pan — No. 7',
    chapter: 'The Company',
    subtitle: 'The Wave',
    audioUrl: null,
    beatSheet: [
      'Patchwork coat, too-bright ribbons, eyes with no age.',
      'Not flying. Held.',
      'The seventh boy to wear the title.',
      'A mudlark who thought eternal life was escape from the factory.',
    ],
    sections: [],
  },
  {
    id: 8,
    title: 'Bio · Tink — Tink Is a Verb',
    chapter: 'The Company',
    subtitle: 'The Tinkerer',
    audioUrl: null,
    beatSheet: [
      'Italian Romani. High priestess, engineer, not a fairy.',
      'Barefoot. Tuning fork.',
      'TINK is a verb. She tinked — past tense — built the bowl.',
      'Ti lascio andare. I let you go.',
    ],
    sections: [],
  },
  {
    id: 9,
    title: 'Bio · Mary — She Does Not Clap',
    chapter: 'The Company',
    subtitle: 'The Bridge',
    audioUrl: null,
    beatSheet: [
      "Clerk's daughter. Mends match-factory uniforms.",
      'First person who will see color in both worlds.',
      'Coat in color, shadow in noir.',
      'She does not clap. She throws the bowl in the swamp.',
    ],
    sections: [],
  },
  {
    id: 10,
    title: 'Bio · Hook — Anchor, Never Weapon',
    chapter: 'The Company',
    subtitle: 'The Particle',
    audioUrl: null,
    beatSheet: [
      'First Pan Tink fell in love with.',
      'Not a pirate. Not a villain. Police of the other side.',
      'Crude iron hook, not ornate.',
      'The hook was an anchor so he could not fly back and die as wave.',
    ],
    sections: [],
  },
  {
    id: 11,
    title: 'Tink Is a Verb',
    chapter: 'The Company · Tink',
    subtitle: 'Ti lascio andare — I let you go',
    audioUrl: 'https://customer-assets-jai6qajn.emergentagent.net/job_author-hub-59/artifacts/zgdlvmmd_Tink_Is_a_Verb.wav',
    beatSheet: [
      'Italian Romani. High priestess, engineer, not a fairy.',
      'Barefoot. Tuning fork.',
      'TINK is a verb. She tinked — past tense — built the bowl.',
      'She saw her father\'s broken heart trying to fetch her mother Liora through endless doors.',
      'She swore Peter would not have that heart. So she trapped him, thinking she was saving him.',
      'She could sing backward all day to open.',
      'She could never sing A forwards while the wheel was stopped and mean it.',
      'So be it. Stay alone forever.',
      'Ti lascio andare. I let you go.',
    ],
    sections: [
      {
        type: 'verse',
        title: 'Verse · The Tinkerer',
        lines: [
          'TINK is a verb, not a name they gave,',
          'She tinked the bowl from a father\'s grave.',
          'Liora gone, the doors kept spinning,',
          'So she stopped the wheel at the beginning.',
          '',
          'Barefoot priestess, tuning fork in hand,',
          'The only one who understands.',
          'Sing backward to open, forward to close,',
          'But A is the letter that nobody chose.',
        ],
      },
      {
        type: 'chorus',
        title: 'Chorus · Ti lascio andare',
        lines: [
          'Ti lascio andare — I let you go.',
          'The wheel keeps turning, the sand keeps low.',
          'She could trap him forever in the cymbal\'s ring,',
          'But love is the one note she could never sing.',
        ],
      },
    ],
  },
];

function AudioPlayer({ src, title }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = React.useRef(null);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setProgress(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    if (audioRef.current) {
      audioRef.current.currentTime = percentage * duration;
    }
  };

  const formatTime = (time) => {
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex items-center gap-4 p-4 rounded-lg" style={{ background: 'var(--dp-iron)' }}>
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
      />
      <button
        onClick={togglePlay}
        className="w-12 h-12 rounded-full flex items-center justify-center transition-all hover:scale-105"
        style={{ background: 'var(--dp-everlit)', color: 'var(--dp-soot)' }}
        data-testid={`play-${title}`}
      >
        {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-1" />}
      </button>
      <div className="flex-1">
        <div
          className="h-2 rounded-full cursor-pointer"
          style={{ background: 'rgba(232, 223, 208, 0.2)' }}
          onClick={handleSeek}
        >
          <div
            className="h-full rounded-full transition-all"
            style={{
              background: 'var(--dp-everlit)',
              width: duration ? `${(progress / duration) * 100}%` : '0%',
            }}
          />
        </div>
        <div className="flex justify-between mt-1 text-xs" style={{ color: 'var(--dp-fog)' }}>
          <span>{formatTime(progress)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
}

export default function LyricsPage() {
  const [selectedSong, setSelectedSong] = useState(SONGS_DATA[0]);

  return (
    <Shell>
      <Helmet>
        <title>Lyrics | The Dark Pan | MGN1.6</title>
        <meta name="description" content="Full lyrics for The Dark Pan songs. A cymatic fairy tale. Two timelines, one story. There were 13 tones. We kept 12 to spin forever." />
        <meta property="og:title" content="Lyrics | The Dark Pan" />
        <meta property="og:description" content="Full lyrics for The Dark Pan songs. Two timelines, one story." />
        <meta property="og:image" content={`${SITE_URL}/stills/battersea.jpg`} />
        <meta property="og:url" content={`${SITE_URL}/dark-pan/lyrics`} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8" data-testid="lyrics-page">
        <Link to="/dark-pan" className="text-sm mb-8 inline-block" style={{ color: 'var(--dp-everlit)' }}>
          ← Back to The Dark Pan
        </Link>
        <span className="mx-3 text-sm" style={{ color: 'var(--dp-fog)' }}>|</span>
        <Link to="/" className="text-sm mb-8 inline-block" style={{ color: 'var(--dp-everlit)' }}>
          ← Back to MGN1.6
        </Link>

        <h1 className="text-4xl sm:text-5xl mb-4" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>
          Lyrics
        </h1>
        <p className="text-lg mb-8" style={{ color: 'var(--dp-fog)' }}>
          Pick a song. Only that one stays on the page.
        </p>

        {/* Song Selector */}
        <div className="flex flex-wrap gap-2 mb-12" data-testid="song-selector">
          {SONGS_DATA.map((song) => (
            <button
              key={song.id}
              onClick={() => setSelectedSong(song)}
              className={`px-4 py-2 rounded-full text-sm transition-all ${
                selectedSong.id === song.id ? 'scale-105' : 'hover:scale-102'
              }`}
              style={{
                background: selectedSong.id === song.id ? 'var(--dp-everlit)' : 'var(--dp-iron)',
                color: selectedSong.id === song.id ? 'var(--dp-soot)' : 'var(--dp-paper)',
                border: '1px solid rgba(232, 223, 208, 0.2)',
              }}
              data-testid={`song-btn-${song.id}`}
            >
              {song.id}. {song.title}
            </button>
          ))}
        </div>

        {/* Selected Song Content */}
        <div className="space-y-8" data-testid="selected-song">
          {/* Audio Player */}
          {selectedSong.audioUrl && (
            <AudioPlayer src={selectedSong.audioUrl} title={selectedSong.title} />
          )}

          {/* Beat Sheet */}
          <div className="p-6 rounded-xl" style={{ background: 'var(--dp-iron)', border: '1px solid rgba(232, 223, 208, 0.1)' }}>
            <p className="text-xs tracking-[0.28em] uppercase mb-2" style={{ color: 'var(--dp-everlit)' }}>
              Beat sheet
            </p>
            <p className="text-sm mb-4" style={{ color: 'var(--dp-fog)' }}>
              {selectedSong.chapter}
            </p>
            <h2 className="text-2xl sm:text-3xl mb-2" style={{ fontFamily: 'var(--dp-font-display)', color: 'var(--dp-bone)' }}>
              {selectedSong.subtitle}
            </h2>
            <h3 className="text-xl mb-6" style={{ color: 'var(--dp-everlit)' }}>
              Song {selectedSong.id} · {selectedSong.title}
            </h3>
            <ol className="space-y-2">
              {selectedSong.beatSheet.map((beat, idx) => (
                <li key={idx} className="flex gap-3 text-sm" style={{ color: 'var(--dp-paper)' }}>
                  <span className="text-xs font-mono opacity-50" style={{ minWidth: '2rem' }}>
                    {String(idx + 1).padStart(2, '0')}.
                  </span>
                  {beat}
                </li>
              ))}
            </ol>
          </div>

          {/* Lyrics Sections */}
          {selectedSong.sections.length > 0 && (
            <div className="space-y-6">
              {selectedSong.sections.map((section, idx) => (
                <div
                  key={idx}
                  className="p-6 rounded-xl"
                  style={{ background: 'var(--dp-iron)', border: '1px solid rgba(232, 223, 208, 0.1)' }}
                >
                  <h4 className="text-sm tracking-[0.15em] uppercase mb-4" style={{ color: 'var(--dp-everlit)' }}>
                    {section.title}
                  </h4>
                  <div className="space-y-2">
                    {section.lines.map((line, i) => (
                      <p key={i} style={{ color: line === '' ? 'transparent' : 'var(--dp-paper)' }}>
                        {line || '\u00A0'}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}
